import React, { useCallback, useEffect, useState } from 'react';
import Button from '../Button';
import { policyService } from '../../api';
import type { PolicyViewModel } from '../../api/types/policy';
import { readFriendlyApiErrorMessage } from '../../utils/apiErrorMessages';

export interface PolicyAcceptanceModalProps {
  policyId: string;
  policySlug?: string | null;
  prefetchedPolicy?: PolicyViewModel | null;
  requireAcceptance?: boolean;
  onAccepted: () => void;
  onClose: () => void;
}

/**
 * Modal for reviewing and accepting an updated platform policy (RuleUpdate notifications).
 */
const PolicyAcceptanceModal: React.FC<PolicyAcceptanceModalProps> = ({
  policyId,
  policySlug,
  prefetchedPolicy,
  requireAcceptance = false,
  onAccepted,
  onClose,
}) => {
  const [policy, setPolicy] = useState<PolicyViewModel | null>(prefetchedPolicy ?? null);
  const [loading, setLoading] = useState(!prefetchedPolicy);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (prefetchedPolicy) {
      setPolicy(prefetchedPolicy);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const numericId = Number(policyId);
        let loaded: PolicyViewModel | null = null;
        if (!Number.isNaN(numericId)) {
          const byId = await policyService.getById(numericId);
          if (byId.isSuccess && byId.data) loaded = byId.data;
        }
        if (!loaded && policySlug) {
          const bySlug = await policyService.getByRouteSlug(policySlug);
          if (bySlug.isSuccess && bySlug.data) loaded = bySlug.data;
        }
        if (!loaded) {
          const terms = await policyService.getTermsOfService();
          if (terms.isSuccess && terms.data) loaded = terms.data;
        }
        if (!cancelled) {
          setPolicy(loaded);
          if (!loaded) setError('Sözleşme içeriği yüklenemedi.');
        }
      } catch (err) {
        if (!cancelled) {
          setError(readFriendlyApiErrorMessage(err, 'Sözleşme yüklenirken bir hata oluştu.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [policyId, policySlug, prefetchedPolicy]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !requireAcceptance) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [onClose, requireAcceptance]);

  const handleAccept = useCallback(async () => {
    const numericId = policy?.id ?? Number(policyId);
    if (Number.isNaN(numericId)) {
      setError('Geçersiz sözleşme.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await policyService.acceptPolicy(numericId);
      if (!result.isSuccess) {
        setError(result.message ?? 'Onay kaydedilemedi.');
        return;
      }
      onAccepted();
      if (!requireAcceptance) onClose();
    } catch (err) {
      setError(readFriendlyApiErrorMessage(err, 'Onay kaydedilemedi.'));
    } finally {
      setSubmitting(false);
    }
  }, [onAccepted, onClose, policy?.id, policyId, requireAcceptance]);

  const title = policy?.name?.trim() || 'Güncellenmiş Sözleşme';

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="policyAcceptModalTitle"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="policyAcceptModalTitle">
                {title}
              </h5>
              {!requireAcceptance ? (
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Kapat"
                  onClick={onClose}
                  disabled={submitting}
                />
              ) : null}
            </div>
            <div className="modal-body">
              {loading ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor…</span>
                  </div>
                </div>
              ) : error && !policy ? (
                <div className="alert alert-warning mb-0" role="alert">
                  {error}
                </div>
              ) : (
                <>
                  <p className="text-muted small">
                    {requireAcceptance
                      ? `${title} güncellendi. Platformu kullanmaya devam etmek için güncellenmiş metni okuyup onaylayın.`
                      : 'Platform kuralları güncellendi. Devam etmeden önce güncellenmiş metni okuyup onaylayın.'}
                  </p>
                  <div
                    className="policy-content text-secondary border rounded p-3 sb-surface-muted"
                    style={{ maxHeight: '50vh', overflowY: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: policy?.text ?? '' }}
                  />
                  {error ? (
                    <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert">
                      {error}
                    </div>
                  ) : null}
                </>
              )}
            </div>
            <div className="modal-footer">
              {!requireAcceptance ? (
                <Button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Daha Sonra
                </Button>
              ) : null}
              <Button
                type="button"
                className="btn btn-primary"
                onClick={handleAccept}
                disabled={loading || submitting || !policy}
                loading={submitting}
              >
                Okudum, Onaylıyorum
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" aria-hidden="true" />
    </>
  );
};

export default PolicyAcceptanceModal;
